import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { dayjs } from '@tsl-frontend/tsl-utilities';

import { NextLesson } from '@features/dashboard/types';

import { Classroom, LearningSession } from '@types';

interface LastUncompletedSession {
    session?: LearningSession;
}

export interface SessionState {
    current?: LearningSession;
    currentClassroom?: Classroom;
    lastUncompleted?: LastUncompletedSession;
    sessionStarting?: boolean;
    nextLesson?: NextLesson;
}

const initialState: SessionState = {};

const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        currentSessionUpdated: (
            state,
            { payload }: PayloadAction<LearningSession>,
        ) => {
            state.current = payload;
        },
        currentSessionStarting: (
            state,
            { payload }: PayloadAction<boolean>,
        ) => {
            state.sessionStarting = payload;
        },
        currentClassroomUpdated: (
            state,
            { payload }: PayloadAction<Classroom>,
        ) => {
            state.currentClassroom = payload;
        },
        lastUncompletedSessionUpdated: (
            state,
            { payload }: PayloadAction<LearningSession>,
        ) => {
            state.lastUncompleted = {
                ...state.lastUncompleted,
                session: payload,
            };
        },
        nextLessonUpdated: (state, { payload }: PayloadAction<NextLesson>) => {
            state.nextLesson = payload;
        },
    },
});

export const {
    currentSessionUpdated,
    currentSessionStarting,
    currentClassroomUpdated,
    lastUncompletedSessionUpdated,
    nextLessonUpdated,
} = sessionSlice.actions;

export const currentSessionSelector = (state: { session: SessionState }) =>
    state.session.current;

export const currSessionPlannedAtSelector = (state: {
    session: SessionState;
}) =>
    state.session.current
        ? dayjs(state.session.current.session_planned_at)
        : null;

export const currSessionPlannedStartNextSelector = (state: {
    session: SessionState;
}) => {
    if (!state.session.current) {
        return false;
    }

    const diff = dayjs(state.session.current.session_planned_at).diff(
        dayjs(),
        'm',
    );

    return diff > -1 && diff < 30;
};

export const currSessionToEndAtSelector = (state: { session: SessionState }) =>
    state.session.current
        ? dayjs(state.session.current.session_to_end_at)
        : null;

export const currSessionStartingSelector = (state: { session: SessionState }) =>
    state.session.sessionStarting;

export const currClassroomURLSelector = (state: { session: SessionState }) =>
    state.session.currentClassroom?.request_url as string;

export const lastUncompletedSessionSelector = (state: {
    session: SessionState;
}) => state.session.lastUncompleted;

export const nextLessonSelector = (state: { session: SessionState }) =>
    state.session.nextLesson;

export default sessionSlice.reducer;
